import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const PlaidSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const publicToken = searchParams.get('public_token');
    const error = searchParams.get('error');

    if (error) {
      setError('Failed to connect your bank account. Please try again.');
      setIsProcessing(false);
      toast.error('Bank connection failed');
      return;
    }

    if (!publicToken) {
      // If no public token, likely user was redirected here after successful OAuth
      // In this case, the connection is already complete
      toast.success('Bank account connected successfully!');
      setTimeout(() => {
        navigate('/sources');
      }, 2000);
      return;
    }

    // If we have a public token, we need to exchange it
    // This would typically happen in OAuth redirect flow
    // For now, we'll just redirect to accounts page
    setIsProcessing(false);
    toast.success('Bank account connected successfully!');
    setTimeout(() => {
      navigate('/sources');
    }, 2000);
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8">
          <div className="text-center">
            <div className="text-destructive mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Connection Failed</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="space-x-4">
              <Button onClick={() => navigate('/sources')} variant="primary">
                Back to Accounts
              </Button>
              <Button onClick={() => navigate('/sources/integrations')} variant="secondary">
                Try Again
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">
        <div className="text-center">
          {isProcessing ? (
            <>
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Processing Connection</h2>
              <p className="text-muted-foreground">Setting up your bank account connection...</p>
            </>
          ) : (
            <>
              <div className="text-success mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Success!</h2>
              <p className="text-muted-foreground mb-6">Your bank account has been connected successfully.</p>
              <p className="text-sm text-muted-foreground">Redirecting to your accounts...</p>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PlaidSuccessPage; 